import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/common/DataTable';
import { DataForm } from '../components/common/DataForm';
import SearchBar from '../components/common/SearchBar';
import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import { ShieldCheck, Users, ToggleLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { RolesService } from '../services/roles.service';

const COLUMNS = [
  { header: 'Role Name', accessor: 'name' },
  { header: 'Display Name', accessor: 'display_name' },
  { header: 'Description', accessor: 'description' },
  { header: 'Status', accessor: 'status' },
];



export default function Roles() {
  const navigate = useNavigate();
  const [dataList, setDataList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, debouncedQuery } = useDebouncedSearch(() => setCurrentPage(1));
  const itemsPerPage = 8;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await RolesService.getAllRoles();
      const list = Array.isArray(res) ? res : (res?.data || []);
      const formatted = list.map(item => ({ ...item, id: item._id || item.id }));
      setDataList(formatted);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = useMemo(() => {
    if (!debouncedQuery) return dataList;
    return dataList.filter(item =>
      Object.values(item).some(val =>
        String(val).toLowerCase().includes(debouncedQuery.toLowerCase())
      )
    );
  }, [dataList, debouncedQuery]);

  const kpis = useMemo(() => {
    const total = dataList.length;
    const active = dataList.filter(d => d.status === 'active').length;
    return [
      { label: 'Total Roles', value: total, icon: <ShieldCheck size={20} />, color: 'blue' },
      { label: 'Active Roles', value: active, icon: <ToggleLeft size={20} />, color: 'green' },
      { label: 'Inactive Roles', value: total - active, icon: <Users size={20} />, color: 'orange' },
    ];
  }, [dataList]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );



  const handleEdit = (row) => {
    const id = row._id || row.id;
    navigate(`/roles/edit/${id}`);
  };

  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    try {
      const targetId = deleteTarget._id || deleteTarget.id;
      await RolesService.deleteRole(targetId);
      setDataList(prev => prev.filter(item => item.id !== targetId));
      toast.success('Role deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete role');
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Roles"
        subtitle="Manage system roles and access levels"
        onAdd={() => navigate('/roles/create')}
        addLabel="Add Role"
        kpis={kpis}
      />

      <div className="table-card-container">
        <div className="table-actions-area">
          <div className="table-actions-left">
            <SearchBar
              placeholder="Search roles..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        <div className="table-responsive-wrapper">
          <DataTable
            columns={COLUMNS}
            data={paginatedData}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            loading={isLoading}
          />
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalEntries={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDeleteExecute}
        title="Delete Role"
        message="Are you sure you want to delete this role? This may affect users assigned to it."
        itemName={deleteTarget ? deleteTarget.display_name || deleteTarget.name : ''}
      />
    </div>
  );
}
